import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { DetailField, SectionCard } from "./shared";
import { fixedDepositStatusLabels } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";

const NEXT_STATUS = { ACTIVE: "MATURED", MATURED: "CLOSED" };

export default function FixedDepositViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fd, setFd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/society/accounting/engine/fixed-deposits/${id}`);
      setFd(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load this fixed deposit"));
      navigate(`${ACCOUNTING_BASE}/fixed-deposits`, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const advanceStatus = async () => {
    const next = NEXT_STATUS[fd.status];
    if (!next) return;
    if (!window.confirm(`Mark this fixed deposit as ${fixedDepositStatusLabels[next]}? This is a one-way master-data status change and does not post any accounting entry.`)) return;
    try {
      setBusy(true);
      await api.post(`/society/accounting/engine/fixed-deposits/${id}/status`, { status: next });
      toast.success(`Fixed deposit marked ${fixedDepositStatusLabels[next]}`);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update status"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!fd) return null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to={`${ACCOUNTING_BASE}/fixed-deposits`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
            &larr; Fixed Deposits
          </Link>
          <h2 className="mt-1 text-lg font-extrabold text-[#111827]">{fd.bankName} - {fd.fdNumber}</h2>
        </div>
        <div className="flex gap-2">
          {NEXT_STATUS[fd.status] && (
            <Button variant="outline" disabled={busy} onClick={advanceStatus}>Mark {fixedDepositStatusLabels[NEXT_STATUS[fd.status]]}</Button>
          )}
          {fd.status === "ACTIVE" && (
            <Button onClick={() => navigate(`${ACCOUNTING_BASE}/fixed-deposits/${id}/edit`)} className="bg-[#111827] text-white hover:bg-[#374151]">
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </Button>
          )}
        </div>
      </div>

      <SectionCard title="Fixed deposit details">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailField label="Instrument / account name" value={fd.instrumentName} />
          <DetailField label="Principal" value={`₹ ${Number(fd.principal || 0).toLocaleString()}`} />
          <DetailField label="Start date" value={fd.startDate} />
          <DetailField label="Maturity date" value={fd.maturityDate} />
          <DetailField label="Interest rate" value={`${fd.interestRate || 0}%`} />
          <DetailField label="Maturity value" value={`₹ ${Number(fd.maturityValue || 0).toLocaleString()}`} />
          <DetailField label="Status" value={fixedDepositStatusLabels[fd.status] || fd.status} />
          <DetailField label="Linked ledger" value={fd.linkedLedgerName} />
        </div>
        {fd.notes && <div className="mt-4"><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Notes</div><p className="mt-1 text-sm text-[#374151]">{fd.notes}</p></div>}
        <p className="mt-4 text-xs text-[#6B7280]">Maturity/interest postings (Dr Bank / Cr Fixed Deposit, interest income) are handled by a later accounting phase - this status change never creates a voucher.</p>
      </SectionCard>

      <SectionCard title="Record history">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailField label="Created" value={fd.createdAt ? new Date(fd.createdAt).toLocaleString() : "-"} />
          <DetailField label="Created by" value={fd.createdBy} />
          <DetailField label="Last updated" value={fd.updatedAt ? new Date(fd.updatedAt).toLocaleString() : "-"} />
          <DetailField label="Updated by" value={fd.updatedBy || fd.createdBy} />
        </div>
      </SectionCard>
    </div>
  );
}
